import { Injectable } from "@angular/core";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { PdfExportSettings } from "@main/reporting.v3/core/reporting-models";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IDashboardPdfTemplateSettings } from "@main/reporting.v3/dashboard/dashboard-models";
import { DashboardAuxiliaryService } from "@main/reporting.v3/dashboard/api/dashboard-auxiliary.service";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { HttpClient } from "@angular/common/http";

@Injectable()
export class DashboardPdfExportingService extends BaseDataService {
    readonly url: string = "api/reporting3/dashboard";

    constructor(private readonly dashboardAuxiliaryService: DashboardAuxiliaryService,
                private readonly htmlRenderingService: HtmlRenderingService,
                http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }

    public async requestPdfExport(dashboardId: string, settings: PdfExportSettings): Promise<string> {
        return this.httpPost(`${dashboardId}/pdf-export`, settings);
    }

    public async getPdfConfiguration(settings: IDashboardPdfTemplateSettings): Promise<JsReportConfiguration> {
        const configurations: JsReportConfiguration[][] =
            await Promise.all(settings.widgetComponents.map(this.getWidgetsPdfConfigurations));

        const format: string = settings.format;

        const template: string = this.getHtmlContent(settings, configurations);
        const cssRules: string = this.getCssRules(settings, configurations);

        const headerTemplate: string = this.getHeaderTemplate(settings);
        const headerCssRules: string = this.getHeaderCssRules(settings);
        const footerTemplate: string = this.getFooterTemplate(settings);
        const footerCssRules: string = this.getFooterCssRules(settings);

        return {
            format: format,
            cssRules: cssRules,
            template: template,
            headerTemplate: headerTemplate,
            headerCssRules: headerCssRules,
            footerTemplate: footerTemplate,
            footerCssRules: footerCssRules,
            marginBottom: 50,
            marginRight: 20,
            marginLeft: 20,
            marginTop: 50,
        };
    }

    private async getWidgetsPdfConfigurations(widgets: IWidgetComponent[]): Promise<JsReportConfiguration[]> {
        const exportable: IPdfExportable[] = widgets.map(widget => (widget as unknown as IPdfExportable));
        return await Promise.all(exportable.map(item => item?.pdfViewVisible ? item.getPdfConfiguration() : null));
    }


    private getHtmlContent(settings: IDashboardPdfTemplateSettings, configurations: JsReportConfiguration[][]): string {
        if (!settings.template?.length) return null;

        const parser: DOMParser = new DOMParser();
        const doc: Document = parser.parseFromString(settings.template, "text/html");

        // Add PDF view container class to dashboard container class list.
        doc.querySelector(this.dashboardContainerSelector)?.classList?.add("pdf-view");

        // Remove the PDF header from the document (it will be added in the correct place by jsReport in the backend).
        doc.querySelector(this.dashboardHeaderSelector)?.parentElement?.remove();

        // Remove the PDF footer from the document (it will be added in the correct place by jsReport in the backend).
        doc.querySelector(this.dashboardFooterSelector)?.parentElement?.remove();

        const rows: NodeListOf<Element> = doc.querySelectorAll(this.widgetRowSelector);
        const rowSeparators: NodeListOf<Element> = doc.querySelectorAll(this.widgetRowSeparatorSelector);

        for (let i = 0; i < settings.widgetComponents.length; i++) {
            const widgetsElements: NodeListOf<Element> = rows[i].querySelectorAll(this.widgetSelector);
            const widgetSeparators: NodeListOf<Element> = rows[i].querySelectorAll(this.widgetSeparatorSelector);

            if (!!configurations[i].some(configuration => !!configuration)) {
                // If at least one widget in this row is visible.
                for (let j = 0; j < settings.widgetComponents[i].length; j++) {
                    const widget: IWidgetComponent = settings.widgetComponents[i][j];
                    const configuration: JsReportConfiguration = configurations[i][j];

                    if (!!configuration) {
                        // If widget is visible, substitute its current HTML with its PDF exporting HTML.
                        const selector: string = `${this.widgetElementSelector} ${widget.widgetSelector}`;
                        const element: Element = widgetsElements[j].querySelector(selector) ?? widgetsElements[j];
                        element.innerHTML = configuration.template ?? "";
                    } else {
                        // Otherwise, remove the widget from view.
                        rows[i].removeChild(widgetsElements[j]);
                        rows[i].removeChild(widgetSeparators[j + 1]);
                    }
                }
            } else {
                // Otherwise, remove this row from view.
                rows[i].parentElement?.removeChild(rows[i]);
                rowSeparators[i + 1].parentElement?.removeChild(rowSeparators[i + 1]);
            }
        }

        return doc.body?.innerHTML;
    }

    private getWidgetsCssRules(settings: IDashboardPdfTemplateSettings, configurations: JsReportConfiguration[][]): string {
        const widgetStyles: Map<string, string> = new Map<string, string>();

        for (let i = 0; i < settings.widgetComponents.length; i++) {
            for (let j = 0; j < settings.widgetComponents[i].length; j++) {
                const widget: IWidgetComponent = settings.widgetComponents[i][j];
                const configuration: JsReportConfiguration = configurations[i][j];

                // If the styles of this component type are already registered, continue;
                if (!configuration || widgetStyles.has(widget.widgetType)) continue;
                widgetStyles.set(widget.widgetType, configuration.cssRules);
            }
        }

        return Array.from(widgetStyles.values()).join("\n")
    }

    private getCssRules(settings: IDashboardPdfTemplateSettings, configurations: JsReportConfiguration[][]): string {
        const dashboardCssRules: string = settings.cssStyle ?? this.dashboardAuxiliaryService.getDashboardDefaultStyles();
        const containerCssRules: string = this.htmlRenderingService.getCssRules(this.dashboardContainerSelector);
        const widgetCssRules: string = this.getWidgetsCssRules(settings, configurations);
        const bodyCssStyles: string = this.htmlRenderingService.getCssRules("body");

        return `${containerCssRules}\n${dashboardCssRules}\n${widgetCssRules}\n${bodyCssStyles}`;
    }

    private getHeaderTemplate(settings: IDashboardPdfTemplateSettings): string {
        const headerHtmlCode: string = settings.headerHtmlCode ?? this.dashboardAuxiliaryService.getDashboardHeaderDefaultHtml();

        return `<div class="${this.dashboardHeaderClass}">${headerHtmlCode}</div>`;
    }

    private getHeaderCssRules(settings: IDashboardPdfTemplateSettings): string {
        const dashboardHeaderCssRules: string = settings.headerCssStyle ?? this.dashboardAuxiliaryService.getDashboardHeaderDefaultStyles();
        const dashboardHeaderRendererCssRules: string = this.dashboardAuxiliaryService.getDashboardHeaderRendererStyles();
        const containerCssRules: string = this.htmlRenderingService.getCssRules(this.dashboardHeaderSelector);

        return `${containerCssRules}\n${dashboardHeaderCssRules}\n${dashboardHeaderRendererCssRules}`;
    }

    private getFooterTemplate(settings: IDashboardPdfTemplateSettings): string {
        const footerHtmlCode: string = settings.footerHtmlCode ?? this.dashboardAuxiliaryService.getDashboardFooterDefaultHtml();

        return `<div class="${this.dashboardFooterClass}">${footerHtmlCode}</div>`;
    }

    private getFooterCssRules(settings: IDashboardPdfTemplateSettings): string {
        const dashboardFooterCssRules: string = settings.footerCssStyle ?? this.dashboardAuxiliaryService.getDashboardFooterDefaultStyles();
        const dashboardFooterRendererCssRules: string = this.dashboardAuxiliaryService.getDashboardFooterRendererStyles();
        const containerCssRules: string = this.htmlRenderingService.getCssRules(this.dashboardFooterSelector);

        return `${containerCssRules}\n${dashboardFooterCssRules}\n${dashboardFooterRendererCssRules}`;
    }


    private get dashboardHeaderClass(): string {
        return this.dashboardAuxiliaryService.dashboardHeaderClass;
    }

    private get dashboardFooterClass(): string {
        return this.dashboardAuxiliaryService.dashboardFooterClass;
    }

    private get dashboardContainerSelector(): string {
        return `.${this.dashboardAuxiliaryService.dashboardContainerClass}`;
    }

    private get dashboardHeaderSelector(): string {
        return `.${this.dashboardAuxiliaryService.dashboardHeaderClass}`;
    }

    private get dashboardFooterSelector(): string {
        return `.${this.dashboardAuxiliaryService.dashboardFooterClass}`;
    }

    private get widgetElementSelector(): string {
        return this.dashboardAuxiliaryService.widgetElementTag;
    }

    private get widgetSelector(): string {
        return `.${this.dashboardAuxiliaryService.widgetClass}`;
    }

    private get widgetRowSelector(): string {
        return `.${this.dashboardAuxiliaryService.widgetRowClass}`;
    }

    private get widgetSeparatorSelector(): string {
        return `.${this.dashboardAuxiliaryService.widgetSeparatorClass}`;
    }

    private get widgetRowSeparatorSelector(): string {
        return `.${this.dashboardAuxiliaryService.widgetRowSeparatorClass}`;
    }
}